import React from 'react';
import { motion } from 'framer-motion';
import { PRICE, offerItems } from '../data/offer';
import type { OfferItem } from '../types/offer';
import { Reveal } from './Reveal';
import { CheckIcon, TrophyIcon, iconMap } from './icons/CraftIcons';
import { CtaButton } from './CtaButton';
function OfferCard({
  item,
  index
}: {
  item: OfferItem;
  index: number;
}) {
  const Icon = iconMap[item.icon];
  const isHero = index === 0;
  return <motion.article initial={{
    opacity: 0,
    y: 28
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.2
  }} transition={{
    duration: 0.55,
    delay: Math.min(index, 5) * 0.06
  }} whileHover={{
    y: -6,
    rotateX: 2,
    rotateY: -2
  }} className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-ivory shadow-soft ${isHero ? 'border-gold sm:col-span-2' : 'border-hairline'}`} data-id="element-438" data-mp="2i0u1jl39r4wk">
      {item.image ? <div className="relative h-40 w-full overflow-hidden border-b border-hairline bg-linen sm:h-44" data-id="element-439" data-mp="35q50cx126p82">
          <img src={item.image} alt={item.imageAlt ?? item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" decoding="async" data-id="element-440" data-mp="1tg4tydherjok" />
          <span className="absolute right-3 top-3 rounded-lg bg-ink/80 px-2.5 py-1 font-mono text-[11px] font-extrabold text-cream" data-id="element-441" data-mp="1ydxwmwji5vk3">
            ${item.value} value
          </span>
        </div> : null}

      <div className="flex flex-1 flex-col p-5" data-id="element-442" data-mp="2850xpycfmajd">
        <div className="flex items-start justify-between gap-3" data-id="element-443" data-mp="286c1hy5vniah">
          <span className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.14em] text-gold-dark" data-id="element-444" data-mp="2m2plbdxvvvs4">
            <Icon className="h-4 w-4 text-sage" data-id="element-445" data-mp="2h5qtx35lu2xm" />
            {item.kicker}
          </span>
          {!item.image ? <span className="rounded-lg bg-linen px-2.5 py-1 font-mono text-[11px] font-extrabold text-ink-soft" data-id="element-446" data-mp="mqwj92w8o08j">
              ${item.value} value
            </span> : null}
        </div>
        <h3 className={`mt-2 font-display font-bold leading-snug text-ink ${isHero ? 'text-xl sm:text-2xl' : 'text-lg'}`} data-id="element-447" data-mp="2rs908gipsj23">
          {item.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-448" data-mp="30sxdva474heg">{item.blurb}</p>
        <span className="mt-4 flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-sage-dark" data-id="element-449" data-mp="30qufo2sw3hyn">
          <CheckIcon className="h-3.5 w-3.5" data-id="element-450" data-mp="3aqv1bve8ea60" />
          Included today
        </span>
      </div>
    </motion.article>;
}
export function ValueStack() {
  return <section aria-labelledby="value-stack" className="w-full bg-cream px-4 py-10 sm:px-6 sm:py-14 lg:py-24" data-id="element-451" data-mp="3fimri4th6dvm">
      <div className="mx-auto max-w-[1120px]" data-id="element-452" data-mp="3oijfs5bydaq5">
        <Reveal className="mx-auto max-w-2xl text-center" data-id="element-453" data-mp="3ojue3pmmpq9v">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/45 bg-gold-tint px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-gold-dark" data-id="element-454" data-mp="3thcq5ovy0lm3">
            <TrophyIcon className="h-3.5 w-3.5" data-id="element-455" data-mp="2h2b4be03eld" />
            Section 4 — The absurd value stack
          </span>
          <h2 id="value-stack" className="mt-4 font-display text-3xl font-bold leading-tight text-ink sm:text-[38px]" data-id="element-456" data-mp="3tmtu8qtd1qyo">
            Everything you get today, priced honestly.
          </h2>
          <p className="mt-3 text-sm text-ink-soft sm:text-base" data-id="element-457" data-mp="q5lj0pt51pcx">
            Eight professional assets. ${PRICE.totalValue} of real-world value. One payment of $
            {PRICE.today}.
          </p>
        </Reveal>

        <div className="perspective-stage mt-7 grid gap-4 sm:mt-10 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3" data-id="element-458" data-mp="gf0kf2dgsku0">
          {offerItems.map((item, index) => <OfferCard key={item.id} item={item} index={index} data-id="element-459" data-mp="18xo2sa12tlre" data-mpi={index} />)}
        </div>

        {/* Ledger total */}
        <Reveal delay={0.1} data-id="element-460" data-mp="145cy6tve5bsi">
          <div className="mt-6 overflow-hidden rounded-3xl border-2 border-gold bg-ivory shadow-lift sm:mt-8" data-id="element-461" data-mp="1454k4ynj80fq">
            <ul className="divide-y divide-dashed divide-hairline px-5 py-2 sm:px-8" data-id="element-462" data-mp="oqr7cfxuxd4d">
              {offerItems.map(item => {
              const Icon = iconMap[item.icon];
              return <li key={item.id} className="flex items-center justify-between gap-4 py-2.5 text-xs text-ink sm:text-sm" data-id="element-463" data-mp="fq2s5hwq96pw">
                    <span className="flex min-w-0 items-center gap-2.5" data-id="element-464" data-mp="17gz48gayr7c4">
                      <Icon className="h-4 w-4 shrink-0 text-sage" data-id="element-465" data-mp="1ccnyc447rw11" />
                      <span className="truncate" data-id="element-466" data-mp="yn3zvaho1kxj">{item.title}</span>
                    </span>
                    <span className="font-mono font-bold text-ink-soft" data-id="element-467" data-mp="12nnua6auuafz">${item.value}</span>
                  </li>;
            })}
            </ul>

            <div className="flex flex-col gap-5 border-t border-hairline bg-gold-tint/60 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8" data-id="element-468" data-mp="1qbnl168620uk">
              <div data-id="element-469" data-mp="3ftu8z0a6nmcv">
                <p className="text-xs font-bold uppercase tracking-wide text-ink-soft" data-id="element-470" data-mp="3ftb1kdi5sgd3">
                  Total real world value
                  <span className="ml-2 font-extrabold text-crimson line-through" data-id="element-471" data-mp="3atycdm3tex7c">
                    ${PRICE.totalValue}.00
                  </span>
                </p>
                <p className="mt-1 text-[11px] font-extrabold uppercase tracking-[0.14em] text-gold-dark" data-id="element-472" data-mp="3km42eqaib4jw">
                  Your price today
                </p>
                <p className="font-display text-5xl font-extrabold leading-none text-gold-dark" data-id="element-473" data-mp="2rxq5yez2ggag">
                  ${PRICE.today}.00
                </p>
              </div>
              <div className="w-full sm:max-w-sm" data-id="element-474" data-mp="31v4bvknd5moi">
                <CtaButton label={`GET THE $${PRICE.totalValue} TREASURY FOR $${PRICE.today}`} subLabel={`You save $${PRICE.savings} · Instant access`} data-id="element-475" data-mp="31ube396du7ii" />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}